import { MonitorCog, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/hooks/useTranslation';

// Constants for better maintainability
const STORAGE_KEY = 'nova-user-theme';
const ANIMATION_DURATION = 300;
const ICON_SIZE_CLASSES = 'h-[1.2rem] w-[1.2rem]';

// Types for better type safety
type ThemeType = 'light' | 'dark' | 'system';

interface ThemeConfig {
  icon: React.ComponentType<{ className?: string }>;
  nextTheme: ThemeType;
  labelKey: string;
  tooltipKey: string;
}

/**
 * Theme toggle component that provides smooth transitions between light, dark, and system themes.
 * Features 360-degree rotation animation and informative tooltips.
 *
 * Behavior:
 * - First visit: Shows system preference with MonitorCog icon
 * - First click: Saves opposite of system preference to localStorage
 * - Subsequent clicks: Cycles through system → light → dark → system
 */
interface ThemeToggleProps {
  /** Button visual variant. Defaults to 'outline' (e.g. login page). */
  variant?: 'default' | 'ghost' | 'outline';
  /** Button size. Defaults to 'icon'. */
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'outline',
  size = 'icon',
}) => {
  const { tDynamic } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [isRotating, setIsRotating] = useState(false);

  // Avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Memoize system preference detection to avoid repeated calculations
  const systemPreference = useMemo((): ThemeType => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }, []);

  // Memoize stored theme to avoid repeated localStorage access
  const storedTheme = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY) || theme;
  }, [theme]);

  // Theme configuration mapping for better maintainability
  const themeConfigs = useMemo(
    (): Record<ThemeType, ThemeConfig> => ({
      light: {
        icon: Sun,
        nextTheme: 'dark',
        labelKey: 'theme.switchToDark',
        tooltipKey: 'theme.lightActive',
      },
      dark: {
        icon: Moon,
        nextTheme: 'system',
        labelKey: 'theme.switchToSystem',
        tooltipKey: 'theme.darkActive',
      },
      system: {
        icon: MonitorCog,
        nextTheme: 'light',
        labelKey: 'theme.switchToLight',
        tooltipKey: 'theme.systemActive',
      },
    }),
    []
  );

  // Optimized theme change handler with useCallback to prevent unnecessary re-renders
  const handleThemeChange = useCallback(() => {
    setIsRotating(true);

    if (!storedTheme) {
      // First time - set opposite of system preference
      const oppositeTheme: ThemeType =
        systemPreference === 'dark' ? 'light' : 'dark';
      setTheme(oppositeTheme);
    } else {
      // Cycle through themes using configuration
      const currentTheme = (theme as ThemeType) || 'system';
      const nextTheme = themeConfigs[currentTheme]?.nextTheme || 'light';
      setTheme(nextTheme);
    }

    // Reset rotation animation
    const timeoutId = setTimeout(() => {
      setIsRotating(false);
    }, ANIMATION_DURATION);

    // Cleanup timeout if component unmounts
    return () => clearTimeout(timeoutId);
  }, [storedTheme, systemPreference, theme, themeConfigs, setTheme]);

  // Memoized icon component to prevent unnecessary re-renders
  const ThemeIcon = useMemo(() => {
    if (!mounted) {
      return <Sun className={ICON_SIZE_CLASSES} />;
    }

    const rotationClass = isRotating ? 'rotate-360' : '';
    const iconClasses = `${ICON_SIZE_CLASSES} ${rotationClass}`.trim();

    // Determine which icon to show
    let IconComponent: React.ComponentType<{ className?: string }>;

    if (!storedTheme) {
      // No stored theme - show system icon
      IconComponent = MonitorCog;
    } else {
      // Use current theme or fallback to system
      const currentTheme = (theme as ThemeType) || 'system';
      IconComponent = themeConfigs[currentTheme]?.icon || MonitorCog;
    }

    return <IconComponent className={iconClasses} />;
  }, [mounted, isRotating, storedTheme, theme, themeConfigs]);

  // Memoized accessibility label
  const ariaLabel = useMemo(() => {
    if (!mounted) return tDynamic('theme.toggleTheme');

    if (!storedTheme) {
      const oppositeTheme = systemPreference === 'dark' ? 'light' : 'dark';
      return tDynamic(
        `theme.switchTo${oppositeTheme.charAt(0).toUpperCase() + oppositeTheme.slice(1)}`
      );
    }

    const currentTheme = (theme as ThemeType) || 'system';
    return (
      tDynamic(themeConfigs[currentTheme]?.labelKey) ||
      tDynamic('theme.toggleTheme')
    );
  }, [mounted, storedTheme, systemPreference, theme, themeConfigs, tDynamic]);

  // Memoized tooltip content
  const tooltipContent = useMemo(() => {
    if (!mounted) return tDynamic('theme.themeSelector');

    if (!storedTheme) {
      return tDynamic('theme.systemPreference', {
        preference: systemPreference,
      });
    }

    const currentTheme = (theme as ThemeType) || 'system';
    return (
      tDynamic(themeConfigs[currentTheme]?.tooltipKey) ||
      tDynamic('theme.themeSelector')
    );
  }, [mounted, storedTheme, systemPreference, theme, themeConfigs, tDynamic]);

  // Loading state - prevent hydration mismatch
  if (!mounted) {
    return (
      <Button variant={variant} size={size} disabled>
        <Sun className={ICON_SIZE_CLASSES} />
        <span className='sr-only'>{tDynamic('theme.loadingSelector')}</span>
      </Button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={size}
          onClick={handleThemeChange}
          aria-label={ariaLabel}
          className='relative'
          type='button'
        >
          {ThemeIcon}
          <span className='sr-only'>{ariaLabel}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltipContent}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export default ThemeToggle;
